import { useTheme } from "@mui/material";
import { DatePicker, type DatePickerProps } from "@mui/x-date-pickers-pro";
import { type DateTime } from "luxon";
import { useCallback } from "react";
import { Field, type FieldProps, type FieldRenderProps } from "react-final-form";

export const FIELD_NAME_DATE = "date";

const tDate = "Date";

export type DateValue = DateTime | null;

type ModifiedDatePickerProps = Omit<DatePickerProps<DateValue>, "localeText" | "onBlur" | "onFocus" | "onChange"> & {
  "data-cy"?: string;
  "data-testid"?: string;
};

export interface DateProps extends Partial<FieldProps<DateValue, FieldRenderProps<DateValue>>> {
  datePickerProps?: ModifiedDatePickerProps;
  delayErrorDisplayUntilTouched?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
}

export const Date = ({
  datePickerProps,
  delayErrorDisplayUntilTouched,
  helperText,
  label = tDate,
  required,
  ...props
}: DateProps) => {
  return (
    <Field<DateValue>
      name={FIELD_NAME_DATE}
      render={function WithField({
        input: { onBlur, onChange, onFocus, value },
        meta: { error, submitFailed, touched },
      }) {
        const theme = useTheme();
        const validationError = !delayErrorDisplayUntilTouched || touched || submitFailed ? error : undefined;

        const handleChange = useCallback<NonNullable<DatePickerProps<DateValue>["onChange"]>>(
          (dateValue) => {
            onChange({ target: { value: dateValue } });
          },
          [onChange],
        );

        return (
          <DatePicker<DateValue>
            desktopModeMediaQuery={theme.breakpoints.up("md")}
            label={label}
            {...datePickerProps}
            onChange={handleChange}
            slotProps={{
              ...datePickerProps?.slotProps,
              textField: {
                error: Boolean(validationError),
                fullWidth: true,
                helperText: validationError || helperText,
                required,
                ...datePickerProps?.slotProps?.textField,
                ...(delayErrorDisplayUntilTouched ? { onBlur, onFocus } : {}),
              },
            }}
            value={value || null}
          />
        );
      }}
      {...props}
    />
  );
};
